import {DOCUMENT, isPlatformBrowser, NgOptimizedImage} from '@angular/common';
import {Component, HostListener, Inject, PLATFORM_ID, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {Meta, Title} from '@angular/platform-browser';
import {ProductService} from '@coreApiServices';
import {UtilityService, ToastService, LoaderService} from '@coreServices';
import {PaginationModel, RequestParamModel} from "@coreModels";
import {CommonConstants, CommonConstants as Constants, ROUTES} from "@coreConstant";
import {ProductListComponent} from 'src/app/core/components/product/product-list/product-list.component';
import {NgbDropdownModule, NgbTooltipModule} from "@ng-bootstrap/ng-bootstrap";
import {FAQsComponent} from "../../../core/components/faqs/faqs.component";
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-product-types-list',
  standalone: true,
  imports: [NgOptimizedImage, RouterModule, ProductListComponent, NgbTooltipModule, NgbDropdownModule, FAQsComponent],
  templateUrl: './product-types-list.component.html',
  styleUrl: './product-types-list.component.scss',
  encapsulation: ViewEncapsulation.None // Use this to apply global styles
})
export class ProductTypesListComponent {
  mainTitle = 'Battens';
  productType: any;
  selectedCollection: string | null = '';
  selectedCategory: string | null = '';
  isMobileResolution!: boolean;
  collectionDetail: any = []
  categoryList: any = []
  isUserAuthenticated: boolean = false;
  isLoading = false;
  protected readonly CommonConstants = CommonConstants;
  showFaqs: boolean = true; // Initially show FAQs
  noImage = CommonConstants.NO_IMAGE_PRODUCT;
  destroy$ = new Subject<void>();

  @HostListener('window:resize', ['$event']) onResize($event: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileResolution = (window.innerWidth <= 1200);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    public utilityService: UtilityService,
    private title: Title,
    private meta: Meta,
    private toastService: ToastService,
    public loaderService: LoaderService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      this.productType = params.get('product-type') || null;
      this.selectedCollection = params.get('collection-name') || null;
      this.selectedCategory = params.get('category-name') || null;
      await this.handleApiCalls();
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileResolution = (window.innerWidth <= 1200);
    }
  }

  async handleApiCalls() {
    try {
      await this.getCollectionDetail();
      this.getCategoryWiseProducts();
    } catch (error) {
      // console.log('error', error);
      // Log or handle error
      if (error) {
        this.toastService.showError(error);
      }
      // Redirect 404 Page
      this.router.navigate(['/' + ROUTES.PAGE_NOT_FOUND], {skipLocationChange: true});
    }
  }

  getCollectionDetail() {
    return new Promise((resolve, reject) => {
      let requestParamModel = new RequestParamModel();
      requestParamModel.pageSize = Constants.PAGE_SIZE_ALL;
      if (this.productType == Constants.FLOORING_TYPE_NAME) {
        requestParamModel.addDefaultFilter('product_type_id', Constants.FLOORING_TYPE_ID, 'number');
      } else {
        requestParamModel.addDefaultFilter('product_type_id', Constants.WALLING_TYPE_ID, 'number');
      }
      if (this.selectedCollection) {
        requestParamModel.addDefaultFilter('slug', this.selectedCollection, 'string');
      }
      requestParamModel.sortChange('name', 'asc');
      requestParamModel.fields = ['id', 'name', 'slug', 'description', 'image', 'meta_title', 'meta_description'];
      this.productService.getProductCollectionList(requestParamModel).pipe(takeUntil(this.destroy$)).subscribe((dataList: PaginationModel<any>) => {
        requestParamModel.update<any>(dataList);
        if (dataList.data && dataList.data[0]) {
          this.collectionDetail = dataList.data[0];
          if (!this.selectedCategory) {
            this.setMetaTags();
            this.setCanonicalURL();
          }
          resolve(true);
        } else {
          reject(false);
        }
      }, (error: HttpErrorResponse) => {
        this.toastService.showError(error);
        // this.loaderService.hide();
        reject(false);
      });
    });
  }

  getCategoryWiseProducts() {
    this.isLoading = true;
    let requestParamModel = new RequestParamModel();
    // Set page size and default filters
    requestParamModel.pageSize = Constants.PAGE_SIZE_ALL;
    requestParamModel.addDefaultFilter('product_collection_id', this.collectionDetail.id, 'number');
    // Add selected category slug if available
    if (this.selectedCategory) {
      requestParamModel.addDefaultFilter('slug', this.selectedCategory, 'number');
    }
    if (this.productType == Constants.FLOORING_TYPE_NAME) {
      requestParamModel.addDefaultFilter('product_type_id', Constants.FLOORING_TYPE_ID, 'number');
    } else {
      requestParamModel.addDefaultFilter('product_type_id', Constants.WALLING_TYPE_ID, 'number');
    }

    // Sort by product name in ascending order
    requestParamModel.sortChange('name', 'asc');

    // Define expanded related entities
    requestParamModel.expands = [
      'products', 'products.availableFinishes', 'products.productMakerMarksIcons', 'products.formatHeroImage', 'products.productImages', 'products.productVideos'
    ];

    // Define fields to be fetched
    requestParamModel.fields = [
      'id',
      'name',
      'meta_title',
      'meta_description',
      'products.id',
      'products.finish_id',
      'products.url_slug',
      'products.family_name',
      'products.material_name',
      'products.finish_edge_name',
      'products.product_category_name',
      'products.product_category_id',
      'products.product_collection_name',
      'products.product_collection_id',
      'products.availableFinishes.id',
      'products.availableFinishes.finish_edge_name',
      'products.availableFinishes.finish_image',
      'products.availableFinishes.hero_finish_product_url_slug',
      'products.availableFinishes.name',
      'products.is_favorite',
      'products.productImages.id',
      'products.productImages.path',
      'products.productVideos.path'
    ];
    // Fetch the product category list
    this.productService.getProductCategoryList(requestParamModel).pipe(takeUntil(this.destroy$)).subscribe(
      (dataList: PaginationModel<any>) => {
        this.isLoading = false;
        if (dataList.data && dataList.data[0]) {
          this.categoryList = dataList.data.map((mainEle: any) => {
            mainEle.products.forEach((ele: any) => {
              ele.newAvailableFinishes = this.chunkArray(ele.availableFinishes);
            });
            return mainEle;
          });
          // this.loaderService.hide();
          // Optional: Set meta tags if a category is selected
          if (this.selectedCategory) {
            this.setMetaTags();
            this.setCanonicalURL();
          }
        } else {
          this.router.navigate(['/' + ROUTES.PAGE_NOT_FOUND], {skipLocationChange: true});
        }
      },
      (error: HttpErrorResponse) => {
        this.isLoading = false;
        // Handle error and stop loading spinner
        this.toastService.showError(error);
        // this.loaderService.hide();
      }
    );
  }

  chunkArray(array?: any) {
    let chunkSize = 5;
    let R = [];
    if (array && array.length > 0) {
      for (var i = 0, len = array.length; i < len; i += chunkSize)
        R.push(array.slice(i, i + chunkSize));
    }
    return R;
  }

  setMetaTags() {
    let metaTitle: any = '';
    let metaDescription: any = '';
    if (this.selectedCategory) {
      if (this.categoryList.length > 0 && this.categoryList[0]?.meta_title) {
        metaTitle = this.categoryList[0]?.meta_title;
      }
    } else {
      if (this.collectionDetail.meta_title != null) {
        metaTitle = this.collectionDetail.meta_title;
      }
      if (this.collectionDetail.meta_description != null) {
        metaDescription = this.collectionDetail.meta_description;
      }
    }
    if (metaTitle) {
      this.title.setTitle(metaTitle);
      this.meta.updateTag({
        name: 'title',
        content: metaTitle
      });
      this.meta.updateTag({
        property: 'og:title',
        content: metaTitle,
      });
    }
    if (metaDescription) {
      this.meta.updateTag({
        name: 'description',
        content: metaDescription,
      });
      this.meta.updateTag({
        property: 'og:description',
        content: metaDescription,
      });
    }

    // Open Graph / Facebook
    this.meta.updateTag({
      property: 'og:url',
      content: Constants.FRONTEND_PREFIX_WITH_WWW + this.router.url,
    });

    if (this.collectionDetail.image) {
      let imageURL = `${CommonConstants.IMAGE_KIT_ENDPOINT}/${this.collectionDetail.image}`;
      this.meta.updateTag({
        property: 'og:image:secure_url',
        content: imageURL,
      });

      this.meta.updateTag({
        property: 'og:image',
        content: imageURL,
      });
    }

  }

  setCanonicalURL() {
    if (isPlatformBrowser(this.platformId)) {
      const head = this.document.getElementsByTagName('head')[0];
      var element: any = this.document.querySelector(`link[rel='canonical']`) || null
      if (element == null) {
        element = this.document.createElement('link') as HTMLLinkElement;
        head.appendChild(element);
      }
      element.setAttribute('rel', 'canonical')
      element.setAttribute('href',environment.HOST_URL + this.router.url)
    }
  }

  redirectWithCategory(categoryName: string) {
    let slug = '/';
    if (this.productType) {
      const type = this.productType === Constants.FLOORING_TYPE_NAME ? Constants.FLOORING_TYPE_NAME : Constants.WALLING_TYPE_NAME;
      slug += this.utilityService.createSlug(type);
    }
    if (this.collectionDetail && this.collectionDetail.name) {
      slug += '/' + this.utilityService.createSlug(this.collectionDetail.name);
    }
    slug += '/' + this.utilityService.createSlug(categoryName);
    return [slug];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
